/**
 * Actions groupées sur la liste des SKILLCARDS (`/tutor/cards`) — PUR, testé.
 *
 * `planBulkCardAction` dit, pour une sélection, quelles cartes l'action touche
 * vraiment et pourquoi les autres sont laissées de côté (jamais d'échec
 * silencieux : l'écran affiche le nombre et la raison).
 *
 * Règles (miroir du cycle de vie et des gardes serveur) :
 * - « Marquer relues » : `generated` → `review` (un brouillon vide n'est pas relisible).
 * - « Repasser en générée » : `review` → `generated`.
 * - « Publier » : `review` ou `published` (republication = nouvel instantané),
 *   comme la RPC `publish_card` ; recto vide ou sans niveau → ignorée.
 * - « Archiver » : tout sauf `archived`.
 * - « Restaurer » : `archived` → `published` si la carte a déjà été publiée
 *   (seule restauration autorisée par le trigger), sinon `generated`.
 */

import type { CardStatus } from '@jeraisonne/db/cards';

export type BulkCardAction = 'review' | 'unreview' | 'publish' | 'archive' | 'restore';

export const BULK_CARD_ACTION_LABELS: Record<BulkCardAction, string> = {
  review: 'Marquer relues',
  unreview: 'Repasser en « Générée »',
  publish: 'Publier',
  archive: 'Archiver',
  restore: 'Restaurer',
};

export interface BulkCardCandidate {
  id: string;
  status: CardStatus;
  sectionCount: number;
  levelCount: number;
  publishedVersion: number | null;
}

export interface BulkCardPlan {
  /** Cartes réellement modifiées, avec leur statut cible (sauf `publish` → RPC). */
  targets: { id: string; nextStatus: CardStatus | null }[];
  /** Cartes laissées de côté, par raison lisible. */
  skipped: { reason: string; count: number }[];
}

function skipReason(action: BulkCardAction, c: BulkCardCandidate): string | null {
  switch (action) {
    case 'review':
      if (c.status !== 'generated') return 'pas au statut « Générée »';
      if (c.sectionCount === 0) return 'recto vide';
      return null;
    case 'unreview':
      return c.status === 'review' ? null : 'pas au statut « Relue »';
    case 'publish':
      if (c.status !== 'review' && c.status !== 'published') return 'pas encore relue';
      if (c.sectionCount === 0) return 'recto vide';
      if (c.levelCount === 0) return 'sans niveau';
      return null;
    case 'archive':
      return c.status === 'archived' ? 'déjà archivée' : null;
    case 'restore':
      return c.status === 'archived' ? null : 'pas archivée';
  }
}

function nextStatus(action: BulkCardAction, c: BulkCardCandidate): CardStatus | null {
  switch (action) {
    case 'review':
      return 'review';
    case 'unreview':
      return 'generated';
    case 'publish':
      return null;
    case 'archive':
      return 'archived';
    case 'restore':
      return c.publishedVersion ? 'published' : 'generated';
  }
}

export function planBulkCardAction(
  action: BulkCardAction,
  cards: readonly BulkCardCandidate[],
): BulkCardPlan {
  const targets: BulkCardPlan['targets'] = [];
  const counts = new Map<string, number>();
  for (const c of cards) {
    const reason = skipReason(action, c);
    if (reason) counts.set(reason, (counts.get(reason) ?? 0) + 1);
    else targets.push({ id: c.id, nextStatus: nextStatus(action, c) });
  }
  return {
    targets,
    skipped: [...counts.entries()].map(([reason, count]) => ({ reason, count })),
  };
}

/** « 12 cartes publiées · 3 ignorées (pas encore relue) ». */
export function formatBulkSummary(
  action: BulkCardAction,
  done: number,
  skipped: BulkCardPlan['skipped'],
  failed = 0,
): string {
  const verb: Record<BulkCardAction, string> = {
    review: 'marquée(s) relue(s)',
    unreview: 'repassée(s) en « Générée »',
    publish: 'publiée(s)',
    archive: 'archivée(s)',
    restore: 'restaurée(s)',
  };
  const parts = [`${done} carte(s) ${verb[action]}`];
  for (const s of skipped) parts.push(`${s.count} ignorée(s) (${s.reason})`);
  if (failed > 0) parts.push(`${failed} en échec`);
  return parts.join(' · ');
}

/** Sélection : bascule un id, ou toutes les cartes visibles d'un coup. */
export function toggleId(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function toggleAll(
  selected: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const allIn = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const next = new Set(selected);
  for (const id of visibleIds) {
    if (allIn) next.delete(id);
    else next.add(id);
  }
  return next;
}
